import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../Datas/siteConfig";
import "../css/productdetails.scss";

const scrollToTop = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => String(item.id) === String(id));
  const {
    addToCart,
    isWishlisted,
    toggleWishlist,
  } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [imageStatus, setImageStatus] = useState({});
  const [selectionError, setSelectionError] = useState("");
  const [addedToBag, setAddedToBag] = useState(false);

  const images = useMemo(() => {
    if (!product) return [];
    return [...new Set([...(product.images || []), product.image].filter(Boolean))];
  }, [product]);

  const colors = useMemo(() => product?.colors || [], [product]);
  const sizes = useMemo(
    () => product?.sizes || product?.size || [],
    [product]
  );
  const swatches = product?.colorSwatches || [];

  useEffect(() => {
    setSelectedSize(sizes[0] || "");
    setSelectedColor(colors[0] || "");
    setQuantity(1);
    setSelectionError("");
    setAddedToBag(false);
    setImageStatus({});
  }, [id, colors, sizes]);

  useEffect(() => {
    if (!product || typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    document.title = `${product.name} — ${siteConfig.brandName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found" id="main-content">
        <div className="product-detail__not-found" role="status">
          <span className="product-detail__error-code">404</span>
          <h1>This piece could not be found</h1>
          <p>It may have moved, sold out, or left our current collection.</p>
          <Link to="/shop" onClick={scrollToTop}>
            Explore the collection
          </Link>
        </div>
      </main>
    );
  }

  const wished = isWishlisted(product.id);
  const description = Array.isArray(product.description)
    ? product.description
    : [product.description].filter(Boolean);
  const detailEntries = Object.entries(product.attributes || {});
  const isSoldOut = String(product.status).toLowerCase() === "sold out";

  const updateImageStatus = (index, status) => {
    setImageStatus((currentStatus) => ({
      ...currentStatus,
      [index]: status,
    }));
  };

  const updateSelection = (setter, value) => {
    setter(value);
    setSelectionError("");
    setAddedToBag(false);
  };

  const handleAddToCart = () => {
    if ((sizes.length > 0 && !selectedSize) || (colors.length > 0 && !selectedColor)) {
      setSelectionError("Choose your preferred colour and size before adding this piece.");
      return;
    }

    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAddedToBag(true);
    setSelectionError("");
  };

  return (
    <main className="product-detail" id="main-content">
      <nav className="product-detail__breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Shop</Link>
        <span aria-hidden="true">/</span>
        <span>{product.category}</span>
      </nav>

      <div className="product-detail__container">
        <section
          className="product-detail__gallery"
          aria-label={`${product.name} gallery`}
          tabIndex="0"
        >
          {images.length > 0 ? (
            images.map((image, index) => {
              const status = imageStatus[index] || "loading";
              return (
                <figure
                  className={`product-detail__image-frame is-${status}`}
                  key={image}
                >
                  {status === "error" ? (
                    <div className="product-detail__image-error" role="img" aria-label="Image unavailable">
                      <span>Image unavailable</span>
                    </div>
                  ) : (
                    <img
                      src={image}
                      alt={`${product.name}, view ${index + 1}`}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      onLoad={() => updateImageStatus(index, "loaded")}
                      onError={() => updateImageStatus(index, "error")}
                    />
                  )}
                </figure>
              );
            })
          ) : (
            <div className="product-detail__image-error" role="img" aria-label="Image unavailable">
              <span>Image unavailable</span>
            </div>
          )}
        </section>

        <aside className="product-detail__info" aria-labelledby="product-title">
          <div className="product-detail__sticky">
            <p className="product-detail__eyebrow">{product.category}</p>
            <h1 id="product-title">{product.name}</h1>

            <div className="product-detail__price" aria-label="Price">
              {product.compareAtPrice && (
                <del>{formatPrice(product.compareAtPrice)}</del>
              )}
              <span>{formatPrice(product.price)}</span>
            </div>

            <div className="product-detail__description">
              {description.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>

            <p className="product-detail__availability">
              <span aria-hidden="true" />
              {product.status || "In stock"} · Made in small editions
            </p>

            {colors.length > 0 && (
              <fieldset className="product-detail__option-group">
                <legend>
                  Colour <span>{selectedColor}</span>
                </legend>
                <div className="product-detail__colour-options">
                  {colors.map((color) => {
                    const swatch = swatches.find((item) => item.name === color);
                    return (
                      <button
                        type="button"
                        className={selectedColor === color ? "is-selected" : ""}
                        key={color}
                        aria-pressed={selectedColor === color}
                        onClick={() => updateSelection(setSelectedColor, color)}
                      >
                        <span
                          className="product-detail__swatch"
                          style={{ backgroundColor: swatch?.hex || "#dedad7" }}
                          aria-hidden="true"
                        />
                        {color}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}

            {sizes.length > 0 && (
              <fieldset className="product-detail__option-group">
                <legend>
                  Size <span>{selectedSize}</span>
                </legend>
                <div className="product-detail__size-options">
                  {sizes.map((size) => (
                    <button
                      type="button"
                      className={selectedSize === size ? "is-selected" : ""}
                      key={size}
                      aria-pressed={selectedSize === size}
                      onClick={() => updateSelection(setSelectedSize, size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="product-detail__purchase-row">
              <div className="product-detail__quantity" aria-label="Quantity selector">
                <button
                  type="button"
                  onClick={() => {
                    setQuantity((currentQuantity) => Math.max(1, currentQuantity - 1));
                    setAddedToBag(false);
                  }}
                  disabled={quantity === 1}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span aria-live="polite">{quantity}</span>
                <button
                  type="button"
                  onClick={() => {
                    setQuantity((currentQuantity) => Math.min(10, currentQuantity + 1));
                    setAddedToBag(false);
                  }}
                  disabled={quantity === 10}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              <button
                type="button"
                className="product-detail__add-button"
                onClick={handleAddToCart}
                disabled={isSoldOut}
              >
                {isSoldOut ? "Sold out" : addedToBag ? "Added to bag" : "Add to bag"}
              </button>

              <button
                type="button"
                className={`product-detail__wishlist-button${wished ? " is-active" : ""}`}
                onClick={() => toggleWishlist(product.id)}
                aria-pressed={wished}
                aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
              >
                <span aria-hidden="true">{wished ? "♥" : "♡"}</span>
              </button>
            </div>

            {selectionError && (
              <p className="product-detail__selection-error" role="alert">
                {selectionError}
              </p>
            )}

            <div className="product-detail__added-message" aria-live="polite">
              {addedToBag && (
                <>
                  This piece is in your bag. <Link to="/cartpage">View bag</Link>
                </>
              )}
            </div>

            <p className="product-detail__contact-note">
              Questions about this piece? Email{" "}
              <a href={`mailto:${siteConfig.contact.customerServiceEmail}`}>
                {siteConfig.contact.customerServiceEmail}
              </a>
              .
            </p>

            <div className="product-detail__accordions">
              <details open>
                <summary>Composition & details</summary>
                <div className="product-detail__accordion-content">
                  <p>{product.material}</p>
                  {detailEntries.length > 0 && (
                    <dl>
                      {detailEntries.map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </details>

              <details>
                <summary>Shipping & returns</summary>
                <div className="product-detail__accordion-content">
                  <p>
                    Standard tracked delivery is a flat {formatPrice(siteConfig.defaultShipping)} per order.
                  </p>
                  <p>
                    Returns are accepted within {siteConfig.returnsWindowDays} days
                    of delivery. <Link to="/shipping">Shipping details</Link> ·{" "}
                    <Link to="/returns">Returns policy</Link>
                  </p>
                </div>
              </details>

              <details>
                <summary>Care guide</summary>
                <div className="product-detail__accordion-content">
                  <p>{product.care}</p>
                  <p>
                    Each piece is finished individually; subtle variations celebrate
                    the hand-painted process.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default ProductDetail;
